// US-UI-MOBILE-LAYOUT -- extracted from HomeScreen so the same per-child
// card body can be reused by:
//   - HomeScreen (desktop) -- renders one card per child in a stacked
//     list as part of the all-in-one dashboard
//   - ChildDetailScreen (mobile) -- renders one child's card as a
//     standalone screen reached from the mobile master list
//
// Keeping this as a single source of truth means action buttons, bucket
// grid presentation, dialogs, and badge counts can't drift between the
// two surfaces. Both render the same tree.
//
// State that's local to one card (deactivate confirmation modal,
// change-relationship modal) lives inside this component so each card
// instance owns its own dialog state. Page-level dialogs (e.g. the
// global approvals queue) stay in their owning screens.

import { type AccountListEntry, type BucketEntry, childDeactivate } from '@chorepop/core/client';
import { type Component, type JSX, Show, createSignal } from 'solid-js';
import { TransientBanner } from '../components/TransientBanner';
import { ConfirmDialog } from './ConfirmDialog';
import { EditGuardianTypeDialog } from './EditGuardianTypeDialog';

type Props = {
  account: AccountListEntry;
  sessionId: string;
  childId: number;
  childName: string;
  childBuckets: BucketEntry[];
  /** Pending-approvals count for THIS child (drives the badge). */
  pendingCount: number;
  /** Bucket grid renderer -- passed in so this component doesn't depend
   *  on HomeScreen's BucketGrid directly. The renderer is given the
   *  same buckets prop as the card itself. */
  renderBucketGrid: () => JSX.Element;
  /** Called after a successful deactivate. Caller is expected to flash
   *  a banner and refetch / navigate. */
  onChildDeactivated: (childDisplayName: string) => void;
  onViewPendingApprovalsForChild: (childAccountId: number) => void;
  onRecordDeposit: (childAccountId: number, childDisplayName: string) => void;
  onViewHistory: (childAccountId: number, childDisplayName: string) => void;
  onViewChores: (childAccountId: number, childDisplayName: string) => void;
  onAddBucket: (
    childAccountId: number,
    childDisplayName: string,
    existingBuckets: BucketEntry[],
  ) => void;
  onResetChildPassword: (childAccountId: number, childDisplayName: string) => void;
  /** US-GIFT-01 -- guardian records a gift for this child. */
  onRecordGift: (childAccountId: number, childDisplayName: string) => void;
  /** US-BUCKET-15 -- guardian directly edits one bucket's balance. */
  onAdjustBucketBalance: (bucket: BucketEntry) => void;
  /** US-BUCKET-16 -- guardian directly transfers between two buckets. */
  onDirectTransfer: (
    childAccountId: number,
    childDisplayName: string,
    buckets: BucketEntry[],
  ) => void;
  /** US-BUCKET-20 -- opens the unified BucketManagerScreen (priority + % + add). */
  onManageBuckets: (
    childAccountId: number,
    childDisplayName: string,
    buckets: BucketEntry[],
  ) => void;
};

export const GuardianChildCard: Component<Props> = (props) => {
  const [pendingDeactivate, setPendingDeactivate] = createSignal<{
    childAccountId: number;
    childDisplayName: string;
  } | null>(null);
  const [deactivating, setDeactivating] = createSignal(false);
  const [deactivateError, setDeactivateError] = createSignal<string | null>(null);

  const [editingType, setEditingType] = createSignal<{
    childAccountId: number;
    childDisplayName: string;
  } | null>(null);
  const [typeFlash, setTypeFlash] = createSignal<string | null>(null);

  async function confirmDeactivate() {
    const target = pendingDeactivate();
    if (!target) return;
    setDeactivating(true);
    setDeactivateError(null);
    try {
      const result = await childDeactivate({
        sessionId: props.sessionId,
        childAccountId: target.childAccountId,
      });
      if (!result.ok) {
        setDeactivateError(result.error.message);
        return;
      }
      props.onChildDeactivated(target.childDisplayName);
      setPendingDeactivate(null);
    } finally {
      setDeactivating(false);
    }
  }

  return (
    <section class="home-card guardian-child-card guardian-child-management">
      <div class="guardian-child-management-heading">
        <div>
          <span class="mobile-home-eyebrow">Child management</span>
          <h2 class="home-child-heading h-display">{props.childName}</h2>
          <p>
            {props.pendingCount} approval{props.pendingCount === 1 ? '' : 's'} waiting
          </p>
        </div>
      </div>

      <nav class="guardian-child-management-tabs" aria-label={`${props.childName} management`}>
        <button
          type="button"
          class="ghost"
          onClick={() => props.onManageBuckets(props.childId, props.childName, props.childBuckets)}
          data-testid={`home-manage-buckets-${props.childId}`}
        >
          Buckets
        </button>
        <button
          type="button"
          class="ghost"
          onClick={() => props.onViewHistory(props.childId, props.childName)}
        >
          Transactions
        </button>
        <button
          type="button"
          class="ghost"
          onClick={() => props.onViewChores(props.childId, props.childName)}
        >
          Chores
        </button>
        <button
          type="button"
          class="ghost approvals-child-button"
          onClick={() => props.onViewPendingApprovalsForChild(props.childId)}
          disabled={props.pendingCount === 0}
          data-testid={`home-approvals-${props.childId}`}
          aria-label={
            props.pendingCount > 0
              ? `Open ${props.pendingCount} pending approvals for ${props.childName}`
              : `No pending approvals for ${props.childName}`
          }
        >
          {props.pendingCount === 0
            ? 'Approvals'
            : `${props.pendingCount} approval${props.pendingCount === 1 ? '' : 's'}`}
        </button>
      </nav>

      <div class="guardian-child-management-tools">
        <section>
          <span class="mobile-home-eyebrow">Money</span>
          <div class="home-child-actions">
            <button
              type="button"
              class="primary"
              onClick={() => props.onRecordDeposit(props.childId, props.childName)}
            >
              Record deposit
            </button>
            <button
              type="button"
              class="ghost"
              onClick={() => props.onRecordGift(props.childId, props.childName)}
              data-testid={`home-record-gift-${props.childId}`}
            >
              Record gift
            </button>
            <button
              type="button"
              class="ghost"
              onClick={() =>
                props.onDirectTransfer(props.childId, props.childName, props.childBuckets)
              }
            >
              Transfer between buckets
            </button>
          </div>
        </section>

        <section>
          <span class="mobile-home-eyebrow">Account</span>
          <div class="home-child-actions">
            <button
              type="button"
              class="ghost"
              onClick={() => props.onResetChildPassword(props.childId, props.childName)}
              data-testid={`child-reset-password-${props.childId}`}
            >
              Reset password
            </button>
            <button
              type="button"
              class="ghost"
              onClick={() =>
                setEditingType({
                  childAccountId: props.childId,
                  childDisplayName: props.childName,
                })
              }
            >
              Change relationship
            </button>
            <button
              type="button"
              class="danger"
              data-testid={`child-deactivate-${props.childId}`}
              onClick={() =>
                setPendingDeactivate({
                  childAccountId: props.childId,
                  childDisplayName: props.childName,
                })
              }
            >
              Delete child account
            </button>
          </div>
        </section>
      </div>
      <Show when={typeFlash()}>
        {(message) => (
          <TransientBanner
            class="home-flash"
            message={message()}
            onDismiss={() => setTypeFlash(null)}
          />
        )}
      </Show>
      {/* Bucket grid is injected by the parent so this component
          doesn't import the (sizeable) BucketGrid implementation
          directly -- keeps the dependency graph clean. */}
      {props.renderBucketGrid()}

      <ConfirmDialog
        when={pendingDeactivate()?.childDisplayName ?? null}
        title={`Delete ${pendingDeactivate()?.childDisplayName ?? 'account'}?`}
        body={`Warning: deleting ${pendingDeactivate()?.childDisplayName ?? 'this account'} removes their chores, buckets, goals, and money history from this ChorePop family. You have 30 days to reclaim the account and its data before deletion becomes permanent.`}
        confirmLabel="Delete account"
        busy={deactivating()}
        error={deactivateError()}
        onConfirm={confirmDeactivate}
        onCancel={() => {
          if (deactivating()) return;
          setPendingDeactivate(null);
          setDeactivateError(null);
        }}
      />

      <EditGuardianTypeDialog
        when={editingType()}
        sessionId={props.sessionId}
        onSaved={(childName, newTypeLabel) => {
          setEditingType(null);
          setTypeFlash(`Relationship with ${childName} set to ${newTypeLabel}.`);
        }}
        onCancel={() => setEditingType(null)}
      />
    </section>
  );
};
